import { useState, useEffect, useCallback } from 'react';
import { X, Download, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

interface EmailAttachmentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailId: number;
  attachmentIndex: number;
  filename: string;
  contentType: string;
}

function isPdfMime(mime: string, filename: string): boolean {
  if (mime === 'application/pdf') return true;
  return filename.toLowerCase().endsWith('.pdf');
}

function isImageMime(mime: string, filename: string): boolean {
  if (mime.startsWith('image/')) return true;
  return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(filename);
}

function isTextMime(mime: string, filename: string): boolean {
  if (mime.startsWith('text/')) return true;
  if (mime === 'application/json' || mime === 'application/xml') return true;
  return /\.(txt|csv|json|xml|md|log)$/i.test(filename);
}

export default function EmailAttachmentPreviewModal({
  isOpen,
  onClose,
  emailId,
  attachmentIndex,
  filename,
  contentType,
}: EmailAttachmentPreviewModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [resolvedMime, setResolvedMime] = useState(contentType || 'application/octet-stream');

  const revokeBlobUrl = useCallback((url: string | null) => {
    if (url) URL.revokeObjectURL(url);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setLoading(true);
      setError(null);
      setTextContent(null);
      setResolvedMime(contentType || 'application/octet-stream');
      setBlobUrl((prev) => {
        revokeBlobUrl(prev);
        return null;
      });
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setTextContent(null);
      setBlobUrl((prev) => {
        revokeBlobUrl(prev);
        return null;
      });

      try {
        const response = await api.get(
          `/email-client/email/${emailId}/attachments/${attachmentIndex}/download`,
          { params: { inline: 1 }, responseType: 'blob' }
        );
        if (cancelled) return;

        const blob: Blob = response.data;
        const mime =
          (typeof blob.type === 'string' && blob.type) ||
          contentType ||
          'application/octet-stream';
        setResolvedMime(mime);

        if (isTextMime(mime, filename) && !isPdfMime(mime, filename) && !isImageMime(mime, filename)) {
          const text = await blob.text();
          if (!cancelled) {
            setTextContent(text);
            setLoading(false);
          }
          return;
        }

        const url = URL.createObjectURL(blob);
        if (!cancelled) {
          setBlobUrl(url);
          setLoading(false);
        } else {
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Email attachment preview failed:', e);
          setError('Anhang konnte nicht geladen werden.');
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, emailId, attachmentIndex, filename, contentType, revokeBlobUrl]);

  useEffect(() => {
    return () => {
      setBlobUrl((prev) => {
        revokeBlobUrl(prev);
        return null;
      });
    };
  }, [revokeBlobUrl]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleDownload = () => {
    if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      return;
    }
    window.open(
      `/api/email-client/email/${emailId}/attachments/${attachmentIndex}/download`,
      '_blank'
    );
  };

  if (!isOpen) return null;

  const isPDF = isPdfMime(resolvedMime, filename);
  const isImage = isImageMime(resolvedMime, filename);
  const isText = isTextMime(resolvedMime, filename);
  const canPreview = (isPDF || isImage || isText) && !error && (blobUrl || textContent !== null);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/70 p-2"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-xl shadow-2xl w-[96vw] max-w-[1400px] h-[92vh] max-h-[92vh] flex flex-col dark:bg-gray-900 dark:shadow-black/40"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -right-2 -top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-md transition-colors hover:bg-gray-100 hover:text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
          title="Schließen (ESC)"
          aria-label="Schließen"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0 dark:border-gray-700">
          <div className="flex items-center gap-2 min-w-0">
            {isPDF && <FileText className="h-5 w-5 text-red-600 flex-shrink-0 dark:text-red-400" />}
            {isImage && <ImageIcon className="h-5 w-5 text-blue-600 flex-shrink-0 dark:text-blue-400" />}
            <span
              className="text-lg font-medium text-gray-900 truncate dark:text-gray-100"
              title={filename}
            >
              {filename}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <Download className="h-4 w-4" />
              Herunterladen
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              title="Schließen (ESC)"
              aria-label="Schließen"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden relative flex items-center justify-center bg-gray-100 min-h-0 dark:bg-gray-950">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center p-8">
              <FileText className="h-16 w-16 text-gray-300 mb-4 dark:text-gray-600" />
              <p className="text-gray-600 mb-2 dark:text-gray-400">{error}</p>
              <button type="button" onClick={handleDownload} className="btn btn-primary inline-flex items-center gap-2">
                <Download className="h-4 w-4" />
                Herunterladen
              </button>
            </div>
          )}

          {!loading && !error && canPreview && isPDF && blobUrl && (
            <iframe
              src={blobUrl}
              className="w-full h-full border-0 rounded-b-xl"
              title={filename}
            />
          )}

          {!loading && !error && canPreview && isImage && blobUrl && (
            <img
              src={blobUrl}
              alt={filename}
              className="max-w-full max-h-full object-contain p-4"
            />
          )}

          {!loading && !error && canPreview && isText && textContent !== null && (
            <pre className="w-full h-full overflow-auto p-4 text-sm text-gray-800 whitespace-pre-wrap font-mono bg-white dark:bg-gray-900 dark:text-gray-200">
              {textContent}
            </pre>
          )}

          {!loading && !error && !canPreview && (
            <div className="flex flex-col items-center justify-center p-8">
              <FileText className="h-16 w-16 text-gray-400 mb-4 dark:text-gray-500" />
              <p className="text-gray-600 mb-2 dark:text-gray-400">
                Vorschau für diesen Dateityp nicht verfügbar
              </p>
              <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">{resolvedMime}</p>
              <button type="button" onClick={handleDownload} className="btn btn-primary inline-flex items-center gap-2">
                <Download className="h-4 w-4" />
                Herunterladen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
